/**
 * Module Section Component
 * Renders a category of modules from metadata (metadata-driven)
 */

import React from 'react';
import { Separator } from '@/components/ui/separator';
import { ModuleCard } from './ModuleCard';
import { getIcon } from '../utils/iconMapper';

export function ModuleSection({
  title,
  description,
  modules,
  config,
  onToggle,
  metadata,
  testIdPrefix
}) {
  if (!modules || modules.length === 0) {
    return null;
  }

  return (
    <section>
      <div className="mb-4">
        <h3 className="text-lg heading-font tracking-tight mb-1">{title}</h3>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
      
      <div className="space-y-4">
        {modules.map(module => {
          // Find full metadata for this module
          const moduleMetadata = metadata?.find(m => m.id === module.id);
          const Icon = getIcon(module.id);
          
          // Check if module is disabled due to dependencies
          const isDisabled = checkDependencies(module.id, config);
          const disabledReason = getDisabledReason(module.id, config);
          
          return (
            <ModuleCard
              key={module.id}
              title={moduleMetadata?.name || module.name}
              description={moduleMetadata?.description || module.description}
              icon={Icon}
              enabled={config?.[`${module.id}_enabled`]}
              onToggle={(val) => onToggle(`${module.id}_enabled`, val)}
              disabled={isDisabled}
              disabledReason={disabledReason}
              pricingImpact={moduleMetadata?.monthly_cost || 0}
              badge={moduleMetadata?.monthly_cost === 0 ? 'Included' : moduleMetadata?.tier}
              testId={`${testIdPrefix}-${module.id}`}
            />
          );
        })}
      </div>
    </section>
  );
}

/**
 * Check if a module should be disabled due to missing dependencies
 */
function checkDependencies(moduleId, config) {
  // Web3 features require wallet configuration
  const web3Features = ['nft_receipts', 'sbt_identity', 'crypto_payments'];
  if (web3Features.includes(moduleId) && !config?.wallet_configured) {
    return true;
  }
  
  switch (moduleId) {
    case 'splits_router':
      return !config?.crypto_payments_enabled;
    case 'hermes':
      return !config?.ai_bitsy_enabled || !config?.ai_marina_enabled || !config?.ai_sage_enabled;
    default:
      return false;
  }
}

/**
 * Get reason why a module is disabled
 */
function getDisabledReason(moduleId, config) {
  // Web3 features need wallet
  const web3Features = ['nft_receipts', 'sbt_identity', 'crypto_payments'];
  if (web3Features.includes(moduleId) && !config?.wallet_configured) {
    return 'Configure your hotel wallet above to enable this feature';
  }
  
  switch (moduleId) {
    case 'splits_router':
      return 'Requires Crypto Payment Processing to be enabled first';
    case 'hermes':
      return 'Requires all three AI agents to be enabled';
    default:
      return '';
  }
}
